import assert from "assert"
import * as THREE from "three"
import type { FolderApi } from "tweakpane"

export default class AnimatedModel {
  container: THREE.Scene
  source: THREE.Object3D
  animations: THREE.AnimationClip[]
  mixer?: THREE.AnimationMixer
  debugFolder?: FolderApi
  idleAction?: THREE.AnimationAction
  actions?: THREE.AnimationAction[]
  idleActionName: string
  previousElapsedTime: number
  lastActivatedAction: THREE.AnimationAction | null

  constructor({
    source,
    animations,
    container,
    debugFolder,
    idleActionName = "idle",
  }: {
    source: THREE.Object3D
    animations: THREE.AnimationClip[]
    container: THREE.Scene
    debugFolder?: FolderApi
    idleActionName?: string
    timeScale?: number
  }) {
    this.source = source
    this.animations = animations
    this.container = container
    this.debugFolder = debugFolder
    this.idleActionName = idleActionName
    this.previousElapsedTime = 0
    this.lastActivatedAction = null

    this.loadModel()
  }

  loadModel() {
    if (!this.source) return

    this.container.add(this.source)

    this.mixer = new THREE.AnimationMixer(this.source)

    const idle = this.animations.find(
      (animation) => animation.name.toLowerCase() === this.idleActionName,
    )
    if (idle) {
      this.idleAction = this.mixer.clipAction(idle)
      // Initialize timeScale to ensure correct playback speed
      if (this.idleAction) {
        this.idleAction.timeScale = 1
      }
    } else {
      console.warn("No idle animation found")
    }

    const actions = this.animations.filter(
      (animation) => animation.name.toLowerCase() !== "idle",
    )
    if (actions.length > 0) {
      this.actions = actions
        .map((action) => {
          const clipAction = this.mixer?.clipAction(action)
          if (clipAction) {
            // Initialize timeScale to ensure correct playback speed
            clipAction.timeScale = 1
          }
          return clipAction
        })
        .filter(Boolean) as THREE.AnimationAction[]
    } else {
      console.warn("No actions found")
    }

    // Setup debug UI
    this.debug()
  }

  fadeToAction(name: string, duration: number = 1) {
    // console.log("fadeToAction", name)
    const previousAction = this.getActiveAction()
    const activeAction = this.findAction(name)

    assert(previousAction, "No previous action found")
    assert(activeAction, "No active action found")

    if (previousAction && previousAction !== activeAction) {
      previousAction.fadeOut(duration)
    }

    // Reset the action first, then set timeScale to ensure correct speed
    activeAction.reset()
    // Set timeScale property directly (not just effectiveTimeScale)
    activeAction.timeScale = 1
    activeAction
      .setEffectiveTimeScale(1)
      .setEffectiveWeight(1)
      .fadeIn(duration)
      .play()

    // Track the most recently activated action
    this.lastActivatedAction = activeAction
  }

  synchronizeCrossFade(endActionName: string, duration: number = 1) {
    if (!this.mixer) return

    const onLoopFinished = (event: THREE.AnimationMixerEventMap["loop"]) => {
      assert(this.mixer, "Mixer is not initialized")

      const endAction = this.findAction(endActionName)
      const activeAction = this.getActiveAction()

      assert(endAction, "No end action found")
      assert(activeAction, "No active action found")

      if (this.mixer && event.action === activeAction) {
        this.mixer.removeEventListener("loop", onLoopFinished)

        this.fadeToAction(endActionName, duration)
      }
    }

    this.mixer.addEventListener("loop", onLoopFinished)
  }

  getActiveAction() {
    if (!this.mixer) return null

    // If we have a last activated action that's still running and has weight > 0,
    // prioritize it (this handles cross-fade cases where weight comparison fails)
    if (
      this.lastActivatedAction &&
      this.lastActivatedAction.isRunning() &&
      !this.lastActivatedAction.paused &&
      this.lastActivatedAction.getEffectiveWeight() > 0
    ) {
      return this.lastActivatedAction
    }

    // Fallback: find action with highest weight that's running
    let activeAction: THREE.AnimationAction | null = null
    let maxWeight = 0

    // Check all actions
    this.actions?.forEach((action) => {
      if (action.isRunning() && !action.paused) {
        const weight = action.getEffectiveWeight()
        if (weight > maxWeight) {
          maxWeight = weight
          activeAction = action
        }
      }
    })

    // Check idle action if no other action found
    if (
      !activeAction &&
      this.idleAction &&
      this.idleAction.isRunning() &&
      !this.idleAction.paused &&
      this.idleAction.getEffectiveWeight() > 0
    ) {
      activeAction = this.idleAction
    }

    // If still no action found, use last activated as fallback
    if (!activeAction && this.lastActivatedAction) {
      activeAction = this.lastActivatedAction
    }

    return activeAction
  }

  findAction(actionName: string) {
    if (actionName === "idle") {
      return this.idleAction
    }

    return this.actions?.find((action) => action.getClip().name === actionName)
  }

  activateAllActions() {
    this.actions?.forEach((action, index) => {
      this.setWeight(action, index === 0 ? 1 : 0)
    })

    this.actions?.forEach((action) => {
      action.play()
    })
  }

  startIdleAction() {
    if (!this.idleAction) return

    this.setWeight(this.idleAction, 1)
    this.idleAction.play()
    this.lastActivatedAction = this.idleAction

    this.actions?.forEach((action) => {
      this.setWeight(action, 0)
      action.play()
    })
  }

  startAction(actionName: string) {
    this.actions?.forEach((action) => {
      const clip = action.getClip()
      this.setWeight(
        action,
        clip.name.toLowerCase() === actionName.toLowerCase() ? 1 : 0,
      )
    })

    this.actions?.forEach((action) => {
      action.play()
    })
  }

  deactivateAllActions() {
    this.actions?.forEach((action) => {
      action.stop()
    })
  }

  pauseAllActions() {
    this.actions?.forEach((action) => {
      action.paused = true
    })
  }

  resumeAllActions() {
    this.actions?.forEach((action) => {
      action.paused = false
    })
  }

  setWeight(action: THREE.AnimationAction, weight: number) {
    action.enabled = true
    // Ensure timeScale is set correctly
    action.timeScale = 1
    action.setEffectiveTimeScale(1)
    action.setEffectiveWeight(weight)
  }

  debug() {
    if (!this.debugFolder) {
      return
    }

    const folder = this.debugFolder.addFolder({
      title: "Animated Model",
      expanded: false,
    })

    folder
      .addButton({
        title: "Activate all actions",
      })
      .on("click", () => {
        this.activateAllActions()
      })
    folder
      .addButton({
        title: "Deactivate all actions",
      })
      .on("click", () => {
        this.deactivateAllActions()
      })

    folder.addBlade({
      view: "separator",
    })

    folder
      .addButton({
        title: "Pause all actions",
      })
      .on("click", () => {
        this.pauseAllActions()
      })

    folder
      .addButton({
        title: "Resume all actions",
      })
      .on("click", () => {
        this.resumeAllActions()
      })

    folder.addBlade({
      view: "separator",
    })

    this.actions?.forEach((action) => {
      folder
        .addButton({
          title: `Action ${action.getClip().name}`,
        })
        .on("click", () => {
          this.fadeToAction(action.getClip().name)
        })
    })

    folder.addBlade({
      view: "separator",
    })
  }

  update({ delta }: { delta: number }) {
    if (!this.mixer) return

    this.mixer.update(delta)
  }
}
